import type { Condition, ComparisonOp, Primitive } from '../types/game';
import type { GameState } from '../engine/GameState';

/** Selección del jugador en la mecánica de conclusión. */
export interface ConclusionSelection {
  hypothesis: string;
  evidence: string[];
  measures: string[];
}

/** Contexto de evaluación: estado de la partida + conclusión opcional. */
export interface EvalContext {
  state: GameState;
  conclusion?: ConclusionSelection | null;
  /**
   * Porcentaje (0–100) del marcador educativo, calculado por el motor con la
   * definición del juego. `null` cuando no hay ejercicios evaluables; las
   * comparaciones numéricas sobre `null` no se cumplen nunca.
   */
  exerciseScorePct?: number | null;
}

/** Error lanzado ante una condición desconocida (vocabulario cerrado). */
export class UnknownConditionError extends Error {
  constructor(public readonly condition: unknown) {
    super(
      `Condición desconocida: ${JSON.stringify(condition)}. ` +
        'El motor solo admite el vocabulario declarativo documentado.'
    );
    this.name = 'UnknownConditionError';
  }
}

function compare(op: ComparisonOp, left: unknown, right: Primitive): boolean {
  switch (op) {
    case 'eq':
      return left === right;
    case 'neq':
      return left !== right;
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      // Comparación numérica. Variables ausentes → NaN → siempre false.
      const a = typeof left === 'number' ? left : Number(left);
      const b = typeof right === 'number' ? right : Number(right);
      if (Number.isNaN(a) || Number.isNaN(b)) return false;
      switch (op) {
        case 'gt':
          return a > b;
        case 'gte':
          return a >= b;
        case 'lt':
          return a < b;
        case 'lte':
          return a <= b;
      }
    }
    default: {
      throw new UnknownConditionError({ op });
    }
  }
}

/**
 * Evalúa una condición declarativa contra el contexto.
 *
 * No ejecuta código del JSON: reconoce únicamente las formas del
 * vocabulario admitido y lanza `UnknownConditionError` ante cualquier otra.
 */
export function evaluateCondition(cond: Condition | undefined, ctx: EvalContext): boolean {
  if (cond === undefined) return true;

  if ('all' in cond) return cond.all.every((c) => evaluateCondition(c, ctx));
  if ('any' in cond) return cond.any.some((c) => evaluateCondition(c, ctx));
  if ('not' in cond) return !evaluateCondition(cond.not, ctx);
  if ('always' in cond) return cond.always === true;

  if ('var' in cond) {
    const value = ctx.state.variables[cond.var];
    return compare(cond.op, value, cond.value);
  }
  if ('hasItem' in cond) return ctx.state.items.includes(cond.hasItem);
  if ('hasEvidence' in cond) return ctx.state.evidence.includes(cond.hasEvidence);
  if ('visitedScene' in cond) return ctx.state.visitedScenes.includes(cond.visitedScene);
  if ('objectiveCompleted' in cond)
    return ctx.state.completedObjectives.includes(cond.objectiveCompleted);

  // Condiciones del contexto de conclusión.
  if ('hypothesisIs' in cond) return ctx.conclusion?.hypothesis === cond.hypothesisIs;
  if ('evidenceSelected' in cond)
    return ctx.conclusion?.evidence.includes(cond.evidenceSelected) ?? false;
  if ('measureSelected' in cond)
    return ctx.conclusion?.measures.includes(cond.measureSelected) ?? false;
  if ('selectedEvidenceCount' in cond)
    return compare(
      cond.selectedEvidenceCount.op,
      ctx.conclusion?.evidence.length ?? 0,
      cond.selectedEvidenceCount.value
    );
  if ('selectedMeasuresCount' in cond)
    return compare(
      cond.selectedMeasuresCount.op,
      ctx.conclusion?.measures.length ?? 0,
      cond.selectedMeasuresCount.value
    );

  if ('objectState' in cond)
    return ctx.state.objectStates[cond.objectState] === cond.is;

  if ('contentPresented' in cond)
    return (ctx.state.presentedContents ?? []).includes(cond.contentPresented);

  if ('exerciseScorePct' in cond) {
    const pct = ctx.exerciseScorePct;
    if (pct === undefined || pct === null) return false;
    return compare(cond.exerciseScorePct.op, pct, cond.exerciseScorePct.value);
  }

  throw new UnknownConditionError(cond);
}
